var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
/* Utility functions used across site. */
import { addOnInfo } from '@content/products/extras';
import { DogRecipeInfo } from '@content/products/meals';
import { formatArrayCommaString } from './formatting_utils';
import { DAYS_IN_A_WEEK, DELIVERY_INTERVALS_NUMBER_MAP, DOG_RECIPE_TO_GRAMS_OUT_OF_STOCK, IN_TRANSIT_TRACKING_STATUSES, OPS_INSERTS_AND_PROMOTIONAL_PRODUCT_TYPES, PET_GENDERS, PET_TYPE, PRODUCT_TYPES, RECIPE_MODE, } from '@variables';
var PASSWORD_MIN_LENGTH = 8;
var PASSWORD_MAX_LENGTH = 64;
var MAX_GRAMS_PER_MEAL_BY_PET_TYPE = (_a = {},
    _a[PET_TYPE.DOG] = 400,
    _a[PET_TYPE.CAT] = 110,
    _a);
var STANDARD_MEALS_PER_DAY = [1, 2];
var NUM_MEAL_PACKS_PER_BUNDLE = 7;
/* Build an array of options for meals per day. */
var buildMealsPerDayOptions = function (pet, quantityMultiplier, includeAllOptions, deliveryInterval) {
    var options = [];
    var daysOfFood = DELIVERY_INTERVALS_NUMBER_MAP[deliveryInterval] * DAYS_IN_A_WEEK;
    var gramsPerDay = pet.mealPlanItems.reduce(function (totalGrams, mealPlanItem) { return totalGrams + mealPlanItem.mealBags * mealPlanItem.gramsBucket; }, 0) / daysOfFood;
    var adjustedGramsPerDay = (gramsPerDay / pet.quantityMultiplier) * quantityMultiplier;
    var maxMealsPerDayOption = 10;
    for (var mealsPerDay = 1; mealsPerDay <= maxMealsPerDayOption; mealsPerDay++) {
        // Round to to the nearest whole number to match with how we do it in the
        // _calculate_calories_per_meal_dog or _calculate_calories_per_meal_cat function in Hotpie.
        var gramsPerMeal = Math.round(adjustedGramsPerDay / mealsPerDay);
        if (gramsPerMeal <= MAX_GRAMS_PER_MEAL_BY_PET_TYPE[pet.petType]) {
            options.push(mealsPerDay);
        }
    }
    if (includeAllOptions) {
        return options;
    }
    // If pet is currently having 1 or 2 meals per day and both [1, 2] are within the options, then
    // we want to show only these 2 options.
    if (STANDARD_MEALS_PER_DAY.includes(pet.mealsPerDay) &&
        options.includes(1) &&
        options.includes(2)) {
        return STANDARD_MEALS_PER_DAY;
    }
    // Otherwise, return the only available option, which is what the pet's currently set to.
    return [pet.mealsPerDay];
};
/* Builds an object mapping recipe names to recipe objects.*/
var buildRecipesMap = function (recipes) {
    return recipes.reduce(function (obj, recipe) { return ((obj[recipe.name] = recipe), obj); }, {});
};
/* Calculates number of bundles from meal plan items of pets. */
var calculateNumBundles = function (pets) {
    if (!pets.length) {
        return 0;
    }
    var totalMealBags = 0;
    pets.forEach(function (pet) {
        pet.mealPlanItems.forEach(function (mealPlanItem) {
            totalMealBags += mealPlanItem.mealBags;
        });
    });
    return totalMealBags / NUM_MEAL_PACKS_PER_BUNDLE;
};
/* Gets maximum number of recipes available for selection */
var getMaxNumSelectableRecipes = function (daysOfFood, mealsPerDay, numRecipes) {
    return daysOfFood ? Math.min(Math.floor((daysOfFood * mealsPerDay) / 7), numRecipes) : 0;
};
/* Determines whether to emit an error message for selected recipes .*/
var determineRecipeError = function (recipeMode, selectedRecipes, daysOfFood, mealsPerDay, numRecipes, petName) {
    if (petName === void 0) { petName = null; }
    var recipesLength = selectedRecipes.length;
    if (recipeMode === RECIPE_MODE.ROTATE && recipesLength < 2) {
        return 'Please choose at least 2 recipes to rotate.';
    }
    if (recipeMode === RECIPE_MODE.MIX) {
        var maxMixedRecipesCount = getMaxNumSelectableRecipes(daysOfFood, mealsPerDay, numRecipes);
        if (maxMixedRecipesCount === 1) {
            return "".concat(petName ? petName : 'Your pet', " is not eligible for multiple recipes.");
        }
        if (maxMixedRecipesCount === 2 && recipesLength !== 2) {
            return "Please choose up to 2 recipes to mix for ".concat(petName ? petName : 'your pet', ".");
        }
        if (recipesLength < 2) {
            return "Please choose at least 2 recipes to mix for ".concat(petName ? petName : 'your pet', ".");
        }
        if (recipesLength > maxMixedRecipesCount) {
            return "Please choose a max of ".concat(maxMixedRecipesCount, " recipes to mix for ").concat(petName ? petName : 'your pet', ".");
        }
    }
    return null;
};
/* Returns gender subject, adjective (possessive), and object pronouns */
var getGenderPronouns = function (gender) {
    if (!gender) {
        return {
            subject: 'they',
            adjective: 'their',
            object: 'them',
            noun: 'pet',
        };
    }
    if (gender === PET_GENDERS.FEMALE) {
        return {
            subject: 'she',
            adjective: 'her',
            object: 'her',
            noun: 'girl',
        };
    }
    return {
        subject: 'he',
        adjective: 'his',
        object: 'him',
        noun: 'boy',
    };
};
var getPetNamesString = function (pets) {
    var petNames = pets.map(function (pet) { return pet.name; });
    return petNames.length < 3 ? formatArrayCommaString(petNames) : 'your pets';
};
// Filter to only include non-deleted recipes for the current pet type.
// For pets that already have cat_fish recipe, upon updating meal plan will not remove
// cat_fish recipe since this hack simply hides the selection only.
var getPetRecipes = function (recipes, petType) {
    return recipes.filter(function (row) { return row.petType === petType && row.deleted === false; });
};
var sortNewRecipesFirst = function (a, b) {
    var aIsNew = DogRecipeInfo[a.name].isNew;
    var bIsNew = DogRecipeInfo[b.name].isNew;
    if (aIsNew && !bIsNew) {
        return -1;
    }
    if (!aIsNew && bIsNew) {
        return 1;
    }
    return 0;
};
var sortNewAddonsFirst = function (a, b) {
    var aIsNew = addOnInfo[a.key.name].isNew;
    var bIsNew = addOnInfo[b.key.name].isNew;
    if (aIsNew && !bIsNew) {
        return -1;
    }
    if (!aIsNew && bIsNew) {
        return 1;
    }
    return 0;
};
var isEligibleForHalfPortions = function (currentWeight, weight) {
    return weight >= 40 || currentWeight >= 40;
};
var makeDeepCopy = function (data) {
    return JSON.parse(JSON.stringify(data));
};
var determineSelectedRecipes = function (pet, recipeMode) {
    if (recipeMode === void 0) { recipeMode = pet.recipeMode; }
    if (recipeMode === RECIPE_MODE.MIX) {
        return __spreadArray([], pet.recipeMix, true);
    }
    if (recipeMode === RECIPE_MODE.ROTATE) {
        return __spreadArray([], pet.recipeRotation, true);
    }
    return pet.recipe && typeof pet.recipe === 'string'
        ? [pet.recipe]
        : pet.recipe && typeof pet.recipe === 'object'
            ? [pet.recipe.name]
            : [];
};
var isEven = function (number) {
    return number % 2 === 0;
};
var parseJwt = function (token) {
    var base64Url = token.split('.')[1];
    // Match javascript's implementation of Base64 characters.
    // See https://en.wikipedia.org/wiki/Base64
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    // Base64 decode the JWT and convert characters to UTF-16 before parsing to avoid unicode errors.
    var jsonPayload = decodeURIComponent(window
        .atob(base64)
        .split('')
        .map(function (c) {
        return "%".concat("00".concat(c.charCodeAt(0).toString(16)).slice(-2));
    })
        .join(''));
    return JSON.parse(jsonPayload);
};
var getEstimatedWeeksFoodWillLastText = function (days) {
    if (!days || days < 4) {
        return 'less than 0.5 weeks';
    }
    var numWeeks = days <= 28 ? Math.round((days * 2) / 7) / 2 : Math.round(days / 7);
    return "about ".concat(numWeeks, " week").concat(numWeeks === 1 ? '' : 's');
};
var getEstimatedWeeksFoodWillLastByPortionSize = function (days) {
    return {
        fullPortions: getEstimatedWeeksFoodWillLastText(days),
        halfPortions: getEstimatedWeeksFoodWillLastText(days / 0.5),
        topperPortions: getEstimatedWeeksFoodWillLastText(days / 0.25),
    };
};
/**
 * Returns true if an order's delivery should be considered delayed. Uses the
 * user's local time for comparison unlike the delayed email job, which uses the
 * order address' timezone.
 * @param {Boolean} deleted is order.deleted true.
 * @param {String} trackingStatus an order.trackingStatus, taken from EasyPost.
 * @param {String} deliveryDate an order.deliveryDate. Should be in ISOformat.
 * @return {Boolean} whether the delivery should be considered delayed.
 */
var isDeliveryDelayed = function (deleted, trackingStatus, deliveryDate) {
    if (deleted === true || !IN_TRANSIT_TRACKING_STATUSES.includes(trackingStatus)) {
        return false;
    }
    // Use 6:05 PM local time to match the missing shipments job's definition of a delayed delivery.
    return new Date() > new Date("".concat(deliveryDate, "T18:05"));
};
var getPostTaxFees = function (fees) {
    return fees.filter(function (fee) { return fee.isTaxable === false; });
};
/* Gets min and max valid weight values based on promo code. */
var getMinMaxWeights = function () {
    return {
        DOG: {
            min: 3,
            max: 200,
        },
        CAT: {
            min: 2,
            max: 30,
        },
    };
};
/* Builds an object mapping order pet keys with array of order pet objects.*/
var groupOrderPetMeals = function (orderPetMeals) {
    var grouped = {};
    orderPetMeals.forEach(function (orderPetMeal) {
        if (!(orderPetMeal.pet.id in grouped)) {
            grouped[orderPetMeal.pet.id] = [];
        }
        grouped[orderPetMeal.pet.id].push(orderPetMeal);
    });
    return grouped;
};
/* Gets the meal item from list of items if it exists. */
var getMealItem = function (items) {
    return items.find(function (item) { return item.productType.name === PRODUCT_TYPES.subscriptionMeals; });
};
/* Gets add-on items from list of items. */
var getAddOnItems = function (items) {
    return items.filter(function (item) {
        return (item.productType.name !== PRODUCT_TYPES.subscriptionMeals &&
            !OPS_INSERTS_AND_PROMOTIONAL_PRODUCT_TYPES.includes(item.productType.name));
    });
};
var isOutOfStockRecipe = function (recipe, grams) {
    return (recipe in DOG_RECIPE_TO_GRAMS_OUT_OF_STOCK &&
        DOG_RECIPE_TO_GRAMS_OUT_OF_STOCK[recipe].includes(grams));
};
var petRecipeMixIncludesOOSRecipe = function (petRecipeMix, petGramsBucket) {
    return petRecipeMix.some(function (recipe) { return isOutOfStockRecipe(recipe, petGramsBucket); });
};
/* Validates password meets security constraints.
  OWASP recommends not silently truncating passwords, but does recommend a
  limit to prevent DDoSing via the reset password endpoint.
  https://cheatsheetseries.owasp.org/cheatsheets/Authentication_Cheat_Sheet.html#implement-proper-password-strength-controls

  This means minLength can be set, but setting maxLength should be avoided.
  Should be kept in sync with User model in hotpie.
*/
var isPasswordValid = function (password) {
    return password.length >= PASSWORD_MIN_LENGTH && password.length <= PASSWORD_MAX_LENGTH;
};
export { buildMealsPerDayOptions, buildRecipesMap, calculateNumBundles, determineRecipeError, determineSelectedRecipes, getEstimatedWeeksFoodWillLastText, getEstimatedWeeksFoodWillLastByPortionSize, getAddOnItems, getGenderPronouns, getMaxNumSelectableRecipes, getMealItem, getMinMaxWeights, getPetNamesString, getPetRecipes, getPostTaxFees, groupOrderPetMeals, isEligibleForHalfPortions, isEven, isDeliveryDelayed, isOutOfStockRecipe, isPasswordValid, makeDeepCopy, parseJwt, petRecipeMixIncludesOOSRecipe, PASSWORD_MIN_LENGTH, sortNewRecipesFirst, sortNewAddonsFirst, };
